import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Save, Mail, Clock, Info, Star, Bell } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        notification_email: s.notification_email || "",
        email_enabled: s.email_enabled,
        min_booking_notice_hours: parseInt(s.min_booking_notice_hours) || 24,
        cancellation_deadline_hours: parseInt(s.cancellation_deadline_hours) || 24,
        review_email_enabled: s.review_email_enabled,
        review_send_after_hours: parseInt(s.review_send_after_hours) || 24,
        google_review_url: s.google_review_url || "",
        feedback_email_subject: s.feedback_email_subject || "",
        allow_testimonials: s.allow_testimonials,
        notify_email_enabled: s.notify_email_enabled,
        notify_dashboard_enabled: s.notify_dashboard_enabled,
      });
      toast.success("Instellingen opgeslagen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-pink" size={28} /></div>;

  return (
    <div data-testid="admin-settings" className="max-w-2xl">
      <h1 className="font-display text-4xl font-semibold text-brand-charcoal">Instellingen</h1>
      <p className="text-brand-muted mt-1">Beheer e-mail en boekingsregels.</p>

      <div className="space-y-6 mt-8">
        <div className="bg-white rounded-2xl border border-brand-border p-6">
          <h2 className="font-display text-xl font-medium text-brand-charcoal flex items-center gap-2 mb-1"><Mail size={18} className="text-brand-pink" /> E-mailmeldingen</h2>
          <p className="text-sm text-brand-muted mb-4">Bevestigings- en annulatiemails worden automatisch naar de klant gestuurd.</p>

          <label className="flex items-center justify-between gap-3 py-2">
            <span className="text-sm font-medium text-brand-charcoal">E-mails versturen</span>
            <input type="checkbox" checked={!!s.email_enabled} onChange={(e) => setS({ ...s, email_enabled: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid="settings-email-enabled" />
          </label>

          <div className="mt-3">
            <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Antwoord-adres (reply-to)</label>
            <input type="email" value={s.notification_email || ""} onChange={(e) => setS({ ...s, notification_email: e.target.value })} placeholder="bv. info@nailsrens.be" className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="settings-notification-email" />
            <p className="flex items-start gap-1.5 text-xs text-brand-muted mt-2"><Info size={13} className="mt-0.5 shrink-0" /> Vul hier je zakelijk e-mailadres in zodra je er een hebt. Klanten kunnen dan rechtstreeks op de bevestigingsmail antwoorden.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-brand-border p-6">
          <h2 className="font-display text-xl font-medium text-brand-charcoal flex items-center gap-2 mb-4"><Clock size={18} className="text-brand-pink" /> Boekingsregels</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Minimale boekingstermijn (uren)</label>
              <input type="number" value={s.min_booking_notice_hours} onChange={(e) => setS({ ...s, min_booking_notice_hours: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="settings-min-notice" />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Annuleren tot (uren vooraf)</label>
              <input type="number" value={s.cancellation_deadline_hours} onChange={(e) => setS({ ...s, cancellation_deadline_hours: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="settings-cancel-deadline" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-brand-border p-6">
          <h2 className="font-display text-xl font-medium text-brand-charcoal flex items-center gap-2 mb-1"><Star size={18} className="text-brand-pink" /> Review instellingen</h2>
          <p className="text-sm text-brand-muted mb-4">Vraag klanten automatisch naar hun ervaring na een afgeronde afspraak.</p>

          <label className="flex items-center justify-between gap-3 py-2">
            <span className="text-sm font-medium text-brand-charcoal">Review e-mail versturen</span>
            <input type="checkbox" checked={!!s.review_email_enabled} onChange={(e) => setS({ ...s, review_email_enabled: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid="settings-review-enabled" />
          </label>

          <div className="grid sm:grid-cols-2 gap-4 mt-3">
            <div>
              <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Versturen na (uren)</label>
              <input type="number" value={s.review_send_after_hours ?? 24} onChange={(e) => setS({ ...s, review_send_after_hours: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="settings-review-after" />
            </div>
            <label className="flex items-center justify-between gap-3 sm:pt-7">
              <span className="text-sm font-medium text-brand-charcoal">Testimonials tonen op website</span>
              <input type="checkbox" checked={!!s.allow_testimonials} onChange={(e) => setS({ ...s, allow_testimonials: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid="settings-allow-testimonials" />
            </label>
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Onderwerp feedback-mail</label>
            <input value={s.feedback_email_subject || ""} onChange={(e) => setS({ ...s, feedback_email_subject: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="settings-feedback-subject" />
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Google review URL</label>
            <input value={s.google_review_url || ""} onChange={(e) => setS({ ...s, google_review_url: e.target.value })} placeholder="https://g.page/r/..." className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none" data-testid="settings-google-url" />
            <p className="flex items-start gap-1.5 text-xs text-brand-muted mt-2"><Info size={13} className="mt-0.5 shrink-0" /> Plak hier je Google review-link. De "Laat een Google review achter"-knop werkt zodra deze is ingevuld.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-brand-border p-6">
          <h2 className="font-display text-xl font-medium text-brand-charcoal flex items-center gap-2 mb-1"><Bell size={18} className="text-brand-pink" /> Notificaties</h2>
          <p className="text-sm text-brand-muted mb-4">Ontvang automatisch een melding bij elke nieuwe boeking, annulatie of wijziging.</p>
          <label className="flex items-center justify-between gap-3 py-2">
            <span className="text-sm font-medium text-brand-charcoal">Nieuwe boeking via e-mail</span>
            <input type="checkbox" checked={!!s.notify_email_enabled} onChange={(e) => setS({ ...s, notify_email_enabled: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid="settings-notify-email" />
          </label>
          <label className="flex items-center justify-between gap-3 py-2">
            <span className="text-sm font-medium text-brand-charcoal">Nieuwe boeking in dashboard</span>
            <input type="checkbox" checked={!!s.notify_dashboard_enabled} onChange={(e) => setS({ ...s, notify_dashboard_enabled: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid="settings-notify-dashboard" />
          </label>
          <p className="flex items-start gap-1.5 text-xs text-brand-muted mt-2"><Info size={13} className="mt-0.5 shrink-0" /> Meldingsmails worden gestuurd naar het antwoord-adres hierboven ({s.notification_email || "info.nails.renss@gmail.com"}).</p>
        </div>

        <div className="bg-brand-blush rounded-2xl p-6 text-sm text-brand-charcoal/80">
          <p className="font-medium text-brand-charcoal mb-1">Studio-gegevens</p>
          Nails Renss · Sint-Amelbergastraat 44, 9700 Mater · 0465 00 03 31 · @nails.renss
        </div>
      </div>

      <button onClick={save} disabled={saving} className="mt-6 flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-7 py-3.5 rounded-full disabled:opacity-60" data-testid="settings-save-button">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Instellingen opslaan
      </button>
    </div>
  );
}
